import { getFileNameInfo } from '@flowus/common';
import { FileRegex } from '@flowus/common/regex';
import { fileUploader } from '@flowus/upload';
import type { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { Icon } from 'src/common/components/icon';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { Ring } from 'src/common/components/ring';
import { Tooltip } from 'src/common/components/tooltip';
import { getReadableFileSizeString } from 'src/editor/utils/size-utils';
import { getOwnerPage } from 'src/hooks/block/use-get-owner-page';
import { useUpload } from 'src/hooks/space/use-upload';
import { Modals } from 'src/modals';
import { UploadStatus } from 'src/redux/types';
import { removeUploadInfoByKey, useUploadInfoById } from 'src/services/upload';
import { getFileIcon } from 'src/utils/file';
import { judgeSharePage } from 'src/utils/getPageId';
import { FileNameUiKit } from '../file-name-uikit';
import { getPreviewUrl } from './file-url';
import { Preview } from './preview';
import { getParams } from './retry-util';
import type { UploadTask } from './types';

interface Props {
  task: UploadTask;
}
/** 上传item */
export const UploadItem: FC<Props> = (props) => {
  const uploadInfo = useUploadInfoById(props.task.id);
  const fileInfo = getFileNameInfo(uploadInfo?.name);
  const percent = (uploadInfo?.progress ?? 100) / 100.0;
  const upload = useUpload();
  const history = useHistory();
  const openModal = useOpenModal();

  if (!uploadInfo) return null;
  const openSourcePage = () => {
    if (uploadInfo.pageId) {
      if (uploadInfo.blockId) {
        // 有可能被移动了所以优先使用getOwnerPage
        const pageId = getOwnerPage(uploadInfo.blockId) ?? uploadInfo.pageId;
        history.push(`/${pageId}#${uploadInfo.blockId}`);
      } else {
        history.push(`/${uploadInfo.pageId}`);
      }
    }
  };
  return (
    <div
      className="group flex items-center justify-between animate-hover px-2"
      style={{
        height: uploadInfo.status === UploadStatus.success ? '40px' : '60px',
      }}
      onClick={() => {
        // 如果url过期了就打开所在页面
        if (uploadInfo.status === UploadStatus.success) {
          const url = getPreviewUrl(uploadInfo.uploadId);
          if (!url) {
            openSourcePage();
            return;
          }
          const isSharePage = judgeSharePage();
          // 如果是pdf就独立处理(copy from folder page)
          if (
            (FileRegex.pdf.test(fileInfo.extName) || FileRegex.epub.test(fileInfo.extName)) &&
            uploadInfo.blockId
          ) {
            window.open(`/${isSharePage ? 'share/' : 'preview/'}${uploadInfo.blockId}`);
          } else {
            // 预览
            openModal.modal({
              modalId: Modals.FILE_PREVIEW,
              noAnimation: true,
              content: ({ onCloseModal }) => (
                <div
                  className="w-full h-full flex justify-center items-center"
                  onClick={(event) => {
                    event.target === event.currentTarget && onCloseModal();
                  }}
                >
                  <div className="file-preview absolute flex items-center justify-center w-full overflow-y-hidden text-white h-full">
                    <Preview url={url} extName={fileInfo.extName} />
                  </div>
                  <Icon
                    name={'IcClose'}
                    size="large"
                    className="absolute right-3 top-3"
                    onClick={() => {
                      onCloseModal();
                    }}
                  />
                </div>
              ),
            });
          }
        } else {
          // 正在上传或者失败的就打开所在页面
          openSourcePage();
        }
      }}
    >
      <div className="flex items-center w-[65%]">
        <Icon name={getFileIcon(fileInfo.extName)} size={'middle'} className={'flex-shrink-0'} />
        <div className="ml-2 w-full">
          <FileNameUiKit className="w-full flex items-center text-t2" fileName={uploadInfo.name} />
          {uploadInfo.status !== UploadStatus.success && ( // 没成功会显示已下载大小
            <div className="text-t4 text-grey3">{getReadableFileSizeString(uploadInfo.size)}</div>
          )}
        </div>
      </div>
      <div className="flex items-center flex-shrink-0">
        {/* 上传中 */}
        {uploadInfo.status === UploadStatus.uploading && (
          <>
            <div className="group-hover:hidden text-t2 text-grey3 ">
              上传中 {uploadInfo.progress}%
            </div>
            <Ring
              percent={percent}
              className="group-hover:hidden border-grey7 ml-2"
              ringColorClassName="border-active_color"
            />
            <Tooltip popup="取消并从列表中移除">
              <Icon
                className="group-hover:flex hidden text-grey3"
                name={'IcNoAccess'}
                size="middle"
                onClick={(e) => {
                  e.stopPropagation();
                  openModal.warning({
                    title: '确定取消该任务',
                    cancelText: '继续传输',
                    confirmText: '确定取消',
                    confirm: () => {
                      uploadInfo.uploadId && fileUploader.abort(uploadInfo.uploadId);
                      removeUploadInfoByKey(uploadInfo.key);
                    },
                  });
                }}
              />
            </Tooltip>
          </>
        )}
        {/* 上传失败 */}
        {uploadInfo.status === UploadStatus.failure && (
          <>
            <div className="text-t2 text-red">上传失败</div>
            <div
              className="text-t2 text-active_color ml-2 hover:opacity-90"
              onClick={(e) => {
                e.stopPropagation();
                const params = getParams(uploadInfo.uploadId);
                if (!params) {
                  message.error('未知原因,无法重试');
                  return;
                }
                removeUploadInfoByKey(uploadInfo.key);
                void upload(params);
              }}
            >
              重试
            </div>
            <Tooltip popup="取消并从列表中移除">
              <Icon
                className="ml-2 text-grey3"
                name={'IcNoAccess'}
                size="middle"
                onClick={(e) => {
                  e.stopPropagation();
                  uploadInfo.uploadId && fileUploader.abort(uploadInfo.uploadId);
                  removeUploadInfoByKey(uploadInfo.key);
                }}
              />
            </Tooltip>
          </>
        )}
        {/* 上传成功 */}
        {uploadInfo.status === UploadStatus.success && (
          <>
            <div className="group-hover:hidden text-t2 text-grey3 ">上传完成</div>
            <div className="hidden group-hover:flex items-center">
              <Tooltip popup="打开文件所在页面">
                <Icon
                  name={'IcSearch'}
                  size="middle"
                  className="text-grey3"
                  onClick={(e) => {
                    e.stopPropagation();
                    // 打开到所在页面
                    openSourcePage();
                  }}
                />
              </Tooltip>
              <Tooltip popup="从列表中移除">
                <Icon
                  className="ml-2.5 text-grey3"
                  name={'IcNoAccess'}
                  size="middle"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeUploadInfoByKey(uploadInfo.key);
                  }}
                />
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
